import React from "react";
import PropTypes from "prop-types";

import { UnstyledButton } from "@sportal/cdk/unstyledButton/UnstyledButton";
import { useToggle } from "@sportal/cdk/hooks";

import { ScheduleRule } from "../scheduleRule/ScheduleRule";
import { Icon } from "../../../../../components/icon/Icon";
import { useIntlTranslate } from "../../../../../hooks";
import { RulePopup } from "../rulePopup/RulePopup";
import { useScheduleRuleData } from "../scheduleRule/useScheduleRuleData";
import { useTooltipPosition } from "@sportal/cdk/tooltip";

export const ScheduleRuleListItem = ({
  className,
  schedule,
  schedules,
  onMouseEnter,
  onMouseLeave,
  disabled,
  onScheduleRemove,
  onScheduleChange,
}) => {
  const {
    setReferenceElement,
    setPopperElement,
    setArrowElement,
    styles,
    attributes,
  } = useTooltipPosition("left-start", [
    { name: "offset", options: { offset: [12, 12] } },
    {
      name: "preventOverflow",
      options: {
        altAxis: true,
        padding: 16,
      },
    },
    {
      name: "flip",
      options: {
        fallbackPlacements: ["bottom", "top", "right-start", "auto"],
      },
    },
  ]);
  const translate = useIntlTranslate();

  const { day: selectedDays, timeStart, timeEnd } = schedule;

  const [showPopup, togglePopup] = useToggle(false);
  const allowDelete = schedules.length > 1;

  const { days, duration } = useScheduleRuleData(
    timeStart,
    timeEnd,
    selectedDays
  );
  const a11yInfo = translate("aria_schedule_rule_info", { days, duration });

  return (
    <div
      className={className}
      id={schedule.id}
      role={"listitem"}
      ref={setReferenceElement}
    >
      <ScheduleRule
        timeStart={schedule.timeStart}
        timeEnd={schedule.timeEnd}
        selectedDays={schedule.day}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        useShortNotations={true}
      />
      <div className="rule__actions">
        <UnstyledButton
          className="rule__actions__button edit-schedule"
          onClick={togglePopup}
          aria-label={translate("aria_schedule_edit_rule_with_info", {
            info: a11yInfo,
          })}
          disabled={disabled}
        >
          <Icon icon="fa-regular fa-pencil" />
        </UnstyledButton>
        {allowDelete && (
          <UnstyledButton
            className="fa-regular fa-trash rule__actions__button remove-schedule"
            onClick={onScheduleRemove}
            aria-label={translate("aria_schedule_delete_rule_with_info", {
              info: a11yInfo,
            })}
            disabled={disabled}
          />
        )}
      </div>
      {showPopup && (
        <RulePopup
          setPopperElement={setPopperElement}
          setArrowElement={setArrowElement}
          popperStyles={styles}
          attributes={attributes}
          selectedDays={selectedDays}
          timeStart={timeStart}
          timeEnd={timeEnd}
          isEdit={true}
          togglePopup={togglePopup}
          allowDelete={schedules.length > 1}
          schedules={schedules}
          onRuleChange={onScheduleChange}
          onRuleRemove={onScheduleRemove}
        />
      )}
    </div>
  );
};

ScheduleRuleListItem.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  schedule: PropTypes.object,
  schedules: PropTypes.arrayOf(PropTypes.object),
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onRuleChange: PropTypes.func,
  onRuleRemove: PropTypes.func,
};
